/**
 * ============================================================================
 * RFLXNS Theme — Shared Cart Transport Service
 * ============================================================================
 * This module is the canonical storefront cart transport layer.
 *
 * It centralizes:
 * - add-to-cart requests
 * - cart count synchronization
 * - cart drawer refresh / hydration
 * - success signal dispatching
 * - quick-add modal-aware post-add flows
 *
 * Route-specific UI should call this service instead of re-implementing cart
 * requests or drawer refresh logic.
 * ============================================================================
 */
(function () {
  if (window.rflxnsCartService) return;

  // --------------------------------------------------------------------------
  // Route helpers and element lookups
  // --------------------------------------------------------------------------
  const getRoot = () => window.Shopify?.routes?.root || '/';
  const getCartAddUrl = () => window.routes?.cart_add_url || `${getRoot()}cart/add`;
  const getCartAddJsonUrl = () => `${getRoot()}cart/add.js`;
  const getCartUrl = () => window.routes?.cart_url || `${getRoot()}cart`;
  const getCartJsonUrl = () => `${getRoot()}cart.js`;

  const getDrawer = () => document.querySelector('cart-drawer');

  /**
   * Pass success messaging to the branded cart signal if the shell exposes it.
   */
  const showCartSignal = (message, detail) => {
    if (typeof window.rflxnsShowCartSignal === 'function') {
      window.rflxnsShowCartSignal(message, detail);
    }
  };

  /**
   * Update the standalone cart count badge used outside of Dawn's cart icon
   * fragment rendering.
   */
  const updateStandaloneCartCount = (count) => {
    if (typeof count !== 'number') return;
    const countEl = document.getElementById('cart-count');
    if (countEl) countEl.textContent = count;
  };

  /**
   * Fetch the current cart JSON and mirror its item count into the shell.
   */
  const refreshCartCount = async () => {
    try {
      const response = await fetch(getCartJsonUrl(), {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return null;
      const cartData = await response.json();
      updateStandaloneCartCount(cartData.item_count);
      return cartData;
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  /**
   * Attach section render metadata to a FormData payload when a cart drawer is
   * available so the response can hydrate the drawer in one round trip.
   */
  const prepareFormData = (formData) => {
    const drawer = getDrawer();
    if (!drawer || !(formData instanceof FormData) || typeof drawer.getSectionsToRender !== 'function') {
      return formData;
    }

    if (!formData.has('sections')) {
      formData.append(
        'sections',
        drawer.getSectionsToRender().map((section) => section.id)
      );
    }

    if (!formData.has('sections_url')) {
      formData.append('sections_url', window.location.pathname);
    }

    return formData;
  };

  /**
   * Render the cart drawer directly from section HTML returned by Shopify's
   * add-to-cart response.
   */
  const renderDrawerFromSections = (response) => {
    const drawer = getDrawer();
    if (!drawer || !response?.sections || typeof drawer.renderContents !== 'function') return false;
    drawer.classList.remove('is-empty');
    const innerDrawer = document.getElementById('CartDrawer');
    if (innerDrawer) innerDrawer.classList.remove('is-empty');
    drawer.renderContents(response);
    return true;
  };

  /**
   * Refresh the cart drawer and cart icon by fetching the cart-drawer section
   * markup from Shopify.
   */
  const refreshDrawer = async ({ openDrawer = true } = {}) => {
    const drawer = getDrawer();
    if (!drawer) {
      await refreshCartCount();
      return false;
    }

    try {
      const response = await fetch(`${getCartUrl()}?section_id=cart-drawer`, {
        headers: { Accept: 'text/html' },
      });
      if (!response.ok) return false;

      const html = await response.text();
      const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
      const nextDrawer = parsedHtml.querySelector('#CartDrawer');
      const currentDrawer = document.querySelector('#CartDrawer');

      if (nextDrawer && currentDrawer) {
        currentDrawer.innerHTML = nextDrawer.innerHTML;
      }

      const nextIcon = parsedHtml.querySelector('#shopify-section-cart-icon-bubble');
      const currentIcon = document.getElementById('shopify-section-cart-icon-bubble');
      if (nextIcon && currentIcon) {
        currentIcon.innerHTML = nextIcon.innerHTML;
      }

      const isEmpty = !!parsedHtml.querySelector('.drawer__inner-empty');
      drawer.classList.toggle('is-empty', isEmpty);
      const innerDrawer = document.getElementById('CartDrawer');
      if (innerDrawer) innerDrawer.classList.toggle('is-empty', isEmpty);
      if (openDrawer && typeof drawer.open === 'function') {
        drawer.open();
      }

      await refreshCartCount();
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  /**
   * Normalize post-add drawer rendering, count sync, and optional success
   * signals after any successful add-to-cart request.
   */
  const syncAfterAdd = async (
    response,
    {
      openDrawer = true,
      showSignal: shouldShowSignal = true,
      signalMessage = 'PIECE SECURED',
      signalDetail = 'The vault has been updated.',
      quickAddModal = null,
    } = {}
  ) => {
    const runRender = async () => {
      const rendered = renderDrawerFromSections(response);
      if (!rendered) {
        await refreshDrawer({ openDrawer });
      } else {
        await refreshCartCount();
      }
    };

    if (quickAddModal) {
      document.body.addEventListener(
        'modalClosed',
        () => {
          runRender().catch((error) => console.error(error));
        },
        { once: true }
      );
      quickAddModal.hide(true);
    } else {
      await runRender();
    }

    if (shouldShowSignal) {
      showCartSignal(signalMessage, signalDetail);
    }

    if (typeof window.playSuccessChime === 'function') {
      window.playSuccessChime();
    }

    return response;
  };

  let activeCartOperation = null;
  let lastOperationTime = 0;
  let lastOperationKey = '';

  /**
   * Submit a Shopify product form payload through the shared cart service.
   */
  const addFormData = async (
    formData,
    {
      openDrawer = true,
      showSignal: shouldShowSignal = true,
      quickAddModal = null,
      signalMessage,
      signalDetail,
    } = {}
  ) => {
    const variantId = formData instanceof FormData ? formData.get('id') : '';
    const now = Date.now();
    const opKey = `form_${variantId}`;

    if (activeCartOperation && (now - lastOperationTime < 450) && lastOperationKey === opKey) {
      return activeCartOperation;
    }

    lastOperationTime = now;
    lastOperationKey = opKey;

    const opPromise = (async () => {
      try {
        const preparedData = prepareFormData(formData);
        const config = {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: preparedData,
        };

        const response = await fetch(getCartAddUrl(), config);
        const data = await response.json();

        if (!response.ok || data.status) {
          return { ok: false, data };
        }

        await syncAfterAdd(data, {
          openDrawer,
          showSignal: shouldShowSignal,
          quickAddModal,
          signalMessage,
          signalDetail,
        });

        return { ok: true, data };
      } finally {
        if (activeCartOperation === opPromise) {
          activeCartOperation = null;
        }
      }
    })();

    activeCartOperation = opPromise;
    return opPromise;
  };

  /**
   * Add a specific variant ID through Shopify's JSON cart endpoint.
   */
  const addVariant = async (
    {
      id,
      quantity = 1,
    },
    {
      openDrawer = true,
      showSignal: shouldShowSignal = true,
      signalMessage,
      signalDetail,
    } = {}
  ) => {
    const now = Date.now();
    const opKey = `variant_${id}_${quantity}`;

    if (activeCartOperation && (now - lastOperationTime < 450) && lastOperationKey === opKey) {
      return activeCartOperation;
    }

    lastOperationTime = now;
    lastOperationKey = opKey;

    const opPromise = (async () => {
      try {
        const response = await fetch(getCartAddJsonUrl(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ id, quantity }),
        });

        const data = await response.json();

        if (!response.ok || data.status) {
          return { ok: false, data };
        }

        await syncAfterAdd(data, {
          openDrawer,
          showSignal: shouldShowSignal,
          signalMessage,
          signalDetail,
        });

        return { ok: true, data };
      } finally {
        if (activeCartOperation === opPromise) {
          activeCartOperation = null;
        }
      }
    })();

    activeCartOperation = opPromise;
    return opPromise;
  };

  // Expose a single shared cart API to every route-specific runtime module.
  window.rflxnsCartService = {
    addFormData,
    addVariant,
    prepareFormData,
    refreshCartCount,
    refreshDrawer,
    syncAfterAdd,
    showCartSignal,
  };
})();
